import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Select, Tooltip } from "antd";
import { FaRegStar, FaStar } from "react-icons/fa";
import {
    filterSelectOptionsWithIdAndName,
    getActiveModule,
    getClientId,
    updateClientSettings,
} from "@ts/utils";
import {
    AppDispatch,
    IClient,
    IValueBoolean,
    SubModule,
    TClientSelectOptions,
    TUserdata,
} from "@ts/common-types";
import { showToast } from "@ts/modals";
import { updateDefaultClient } from "@ts/retrieve-data";
import { updateUserData } from "@features/userDataSlice";
import { REVSTACK_RELATIVE_FILTERS } from "@/ts/constants";
import { keys } from "underscore";
import { applyCurrentFiltersWithHash } from "@/ts/revstack/filters";
import { resetAllAdditionalFilters } from "@/features/revstackWidgetsSlice";

interface IClientSelector {
    showDefaultClientButton: boolean;
    className?: string;
    styles?: React.CSSProperties;
    isDashboardModule?: boolean;
}

interface IEnableClientSelection {
    enableClientSelection: IValueBoolean;
}

const { Option } = Select;

const ClientsSelector: React.FC<IClientSelector> = ({
    showDefaultClientButton = true,
    className,
    styles,
    isDashboardModule = false,
}) => {
    const dispatch = useDispatch<AppDispatch>();

    const activeModule = getActiveModule().activeModule;
    const clients = useSelector(
        (state: { clients: { value: IClient[] } }) => state.clients.value
    );
    const enableClientSelection = useSelector(
        (state: IEnableClientSelection) => state.enableClientSelection.value
    );

    const relativeFilters = useSelector((state: any) => state.relativeFilters.value);

    const isClientSelectionDisabled = useMemo(() => {
        const filterKeys = keys(relativeFilters);
        return enableClientSelection || (activeModule === SubModule.PRODUCT_ANALYSIS &&
            filterKeys?.includes(REVSTACK_RELATIVE_FILTERS.SKUS) && relativeFilters?.skus?.length > 0);
    }, [enableClientSelection, activeModule, relativeFilters]);


    const onProfileSelection = async (value: number) => {
        await updateClientSettings(value);

        if (isDashboardModule) {
            dispatch(resetAllAdditionalFilters());
            await applyCurrentFiltersWithHash({
                client: value,
                resetFilters: true
            });
        }
    };

    const userData = useSelector((state: TUserdata) => state.userData.value);
    const defaultClientId = userData?.default_client;
    // useEffect(() => {
    //     const currentClientId = getClientId();
    //     if (currentClientId && clients.length > 0) {
    //         const currentClient = clients.find(
    //             (client) => client.id === currentClientId
    //         );
    //         if (currentClient && typeof currentClient.logo_url === "string") {
    //             dispatch(updateSidebarLogo(currentClient.logo_url));
    //         } else {
    //             fetchAndUpdateLogo(currentClientId);
    //         }
    //     }
    // }, [clients]);

    const handleSetDefaultClient = async (
        clientId: number,
        e: React.MouseEvent
    ) => {
        e.stopPropagation();
        try {
            const response = await updateDefaultClient(clientId);
            if (response && response.status) {
                const updatedUserData = {
                    ...userData,
                    default_client: clientId,
                };
                dispatch(updateUserData(updatedUserData));
                showToast("success", "Default client updated successfully");
            } else {
                showToast("danger", "Failed to update default client");
            }
        } catch (error) {
            showToast("danger", "Failed to update default client");
        }
    };
    return (
        <div id="profiles-selection">
            <Select
                showSearch
                id="profiles-select-box"
                className={className}
                style={styles}
                size="middle"
                value={+getClientId()}
                onChange={onProfileSelection}
                disabled={isClientSelectionDisabled}
                filterOption={(input, option: TClientSelectOptions) => {
                    const childrenProps =
                        option.children?.props?.children[0].props.children ||
                        [];
                    return childrenProps.length
                        ? filterSelectOptionsWithIdAndName(input, childrenProps)
                        : false;
                }}
            >
                <Option key={0} value="0" disabled>
                    -- Select Profile --
                </Option>
                {clients.map((element, index) => {
                    const isDefault = element.id === defaultClientId;
                    return (
                        <Option key={index + 1} value={element.id}>
                            <div
                                className="client-info d-flex align-items-center justify-content-between"
                                style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                                data-country={
                                    element.country_non_iso ||
                                    element.country_iso
                                }
                                data-currency={element.currency}
                                data-timezone={element.timezone}
                            >
                                <div className="d-flex align-items-center">
                                    {`${element.name} (${element.country_non_iso ||
                                        element.country_iso
                                        })`}
                                    <div className="d-none">{`${element.id}##`}</div>
                                </div>
                                {showDefaultClientButton && (
                                    <Tooltip
                                        title={
                                            isDefault
                                                ? "Current Default"
                                                : "Set as Default"
                                        }
                                    >
                                        <Button
                                            type="text"
                                            size="small"
                                            className="p-0 ms-2"
                                            onClick={(e) =>
                                                handleSetDefaultClient(
                                                    element.id,
                                                    e
                                                )
                                            }
                                            disabled={isDefault}
                                        >
                                            {isDefault ? (
                                                <FaStar className="text-warning" />
                                            ) : (
                                                <FaRegStar />
                                            )}
                                        </Button>
                                    </Tooltip>
                                )}
                                <div className="d-none">{`${element.id}##`}</div>
                            </div>
                        </Option>
                    );
                })}
            </Select>
        </div>
    );
};

export default ClientsSelector;
